/**
 * apptDetails — the live note / procedures behind the cards (plan §4b), and which appointments to ask about.
 *
 * WHY THIS EXISTS (2026-10-30). The exe's copy of an appointment's Note and ProcDescript in the board document is
 * only a first paint: its handlers for edits never applied. The card shows what `POST /get-practice-pulse-ops`
 * says (the Open Dental mirror), and falls back to the document's copy until that arrives. This file is the pure
 * side of that:
 *
 *   boardAptNums(board)            every AptNum on the board (Check-in, rooms, Check-out), ascending, unique
 *   boardPatNums(board)            every PatNum on the board, ascending, unique — what the alerts and the task
 *                                  listener care about
 *   missingAptNums(board, have, asked)  the AptNums that are on the board and neither loaded nor already asked for
 *   chunkAptNums(nums, size)       the route takes 1–200 per call
 *   detailsFromReply(reply)        the reply rows → { [AptNum]: row }; garbage rows are skipped
 *   liveCardText(card, details)    { proc, note } for one card
 *
 * LIVE TEXT WINS, EVEN WHEN EMPTY. When the reply has a row for the appointment and the field is a string, that
 * string is what the card says — an office that CLEARED a note in Open Dental must see it disappear, not the
 * document's stale copy. Only a missing row (not loaded yet, or the appointment is not in the mirror) or a
 * non-string field falls back to the card's own.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

/** The route answers 1–200 AptNums per call (practice-pulse-service getPracticePulseOps). */
export const DETAILS_BATCH_SIZE = 200;

function positiveInt(v) {
  const n = typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v.trim()) : v;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
}

function allCards(board) {
  const b = board && typeof board === 'object' ? board : {};
  const out = [];
  (Array.isArray(b.checkin) ? b.checkin : []).forEach((c) => out.push(c));
  (Array.isArray(b.rooms) ? b.rooms : []).forEach((r) => (r && Array.isArray(r.cards) ? r.cards : []).forEach((c) => out.push(c)));
  (Array.isArray(b.checkout) ? b.checkout : []).forEach((c) => out.push(c));
  return out.filter((c) => c && typeof c === 'object');
}

function uniqueSorted(values) {
  return Array.from(new Set(values.filter((v) => v !== null))).sort((a, b) => a - b);
}

export function boardAptNums(board) {
  return uniqueSorted(allCards(board).map((c) => positiveInt(c.aptNum)));
}

export function boardPatNums(board) {
  return uniqueSorted(allCards(board).map((c) => positiveInt(c.patNum)));
}

/** `have` is a map keyed by AptNum (what the page has loaded); `asked` a map of AptNums already requested. */
export function missingAptNums(board, have, asked) {
  const loaded = have && typeof have === 'object' ? have : {};
  const requested = asked && typeof asked === 'object' ? asked : {};
  return boardAptNums(board).filter((n) => !Object.prototype.hasOwnProperty.call(loaded, n) && !requested[n]);
}

export function chunkAptNums(nums, size) {
  const list = (Array.isArray(nums) ? nums : []).map(positiveInt).filter((n) => n !== null);
  const step = Number.isInteger(size) && size > 0 ? size : DETAILS_BATCH_SIZE;
  const out = [];
  for (let i = 0; i < list.length; i += step) out.push(list.slice(i, i + step));
  return out;
}

/** The reply rows → { [AptNum]: row }. Accepts the array or `{ data: array }`; a row without a usable AptNum is skipped. */
export function detailsFromReply(reply) {
  const rows = Array.isArray(reply) ? reply : (reply && Array.isArray(reply.data) ? reply.data : []);
  const out = {};
  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const aptNum = positiveInt(row.AptNum);
    if (aptNum !== null) out[aptNum] = row;
  });
  return out;
}

/** { proc, note } — the live row's strings when it has them, else the card's own. See the header. */
export function liveCardText(card, details) {
  const c = card && typeof card === 'object' ? card : {};
  const aptNum = positiveInt(c.aptNum);
  const row = aptNum !== null && details && typeof details === 'object' ? details[aptNum] : null;
  const pick = (liveValue, own) => (row && typeof liveValue === 'string' ? liveValue : (typeof own === 'string' ? own : ''));
  return {
    proc: pick(row && row.ProcDescript, c.procDescript).trim(),
    note: pick(row && row.Note, c.note),
  };
}
